import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { UserPlus, Trash2, Power, Loader2, Mail, Phone, Pencil } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { formatDateBR } from "../lib/format";
import { PageHeader } from "../components/Layout";
import { Filters } from "../components/Filters";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { ImpersonateButton } from "../components/ImpersonateButton";
import { UserEditDialog } from "../components/UserEditDialog";
import { ConfirmDelete } from "../components/ConfirmDelete";
import { useAuth } from "../context/AuthContext";

const NIVEIS = ["Babá", "Diarista", "Cozinheira", "Jardineiro", "Motorista", "Cuidador(a)", "Governanta", "Colaborador"];
const emptyForm = { name: "", email: "", password: "", nivel: "Babá", phone: "" };

export default function Collaborators() {
    const qc = useQueryClient();
    const [filters, setFilters] = useState({ q: "", start: "", end: "" });
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);
    const [editUser, setEditUser] = useState(null);
    const { user: me } = useAuth();

    const params = new URLSearchParams();
    if (filters.q) params.set("q", filters.q);
    if (filters.start) params.set("start", filters.start);
    if (filters.end) params.set("end", filters.end);

    const { data: list = [] } = useQuery({ queryKey: ["collaborators", filters], queryFn: async () => (await api.get(`/collaborators?${params}`)).data });

    const create = async () => {
        if (!form.name || !form.email || !form.password) { toast.error("Preencha nome, e-mail e senha"); return; }
        setSaving(true);
        try {
            await api.post("/collaborators", form);
            qc.invalidateQueries({ queryKey: ["collaborators"] });
            setOpen(false);
            setForm(emptyForm);
            toast.success("Colaborador cadastrado");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const toggleActive = async (c) => {
        try {
            await api.put(`/collaborators/${c.user_id}`, { active: !c.active });
            qc.invalidateQueries({ queryKey: ["collaborators"] });
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    const remove = async (id) => {
        try {
            await api.delete(`/collaborators/${id}`);
            qc.invalidateQueries({ queryKey: ["collaborators"] });
            toast.success("Colaborador removido");
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    return (
        <div>
            <PageHeader
                overline="Equipe do lar"
                title="Colaboradores"
                description="Cadastre e gerencie sua equipe. Cada colaborador acessa o app com o próprio login."
                action={
                    <Dialog open={open} onOpenChange={setOpen}>
                        <DialogTrigger asChild>
                            <Button className="rounded-full px-6" data-testid="add-collaborator-btn"><UserPlus className="mr-2 h-4 w-4" /> Novo colaborador</Button>
                        </DialogTrigger>
                        <DialogContent className="rounded-2xl sm:max-w-lg">
                            <DialogHeader><DialogTitle className="font-heading text-2xl font-medium">Cadastrar colaborador</DialogTitle></DialogHeader>
                            <div className="space-y-4">
                                <div>
                                    <Label>Nome completo</Label>
                                    <Input data-testid="collab-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 rounded-xl" placeholder="Ex: Maria Silva" />
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <Label>Nível / Função</Label>
                                        <select data-testid="collab-nivel" value={form.nivel} onChange={(e) => setForm({ ...form, nivel: e.target.value })} className="mt-1 w-full rounded-xl border border-input bg-background px-3 py-2 text-sm">
                                            {NIVEIS.map((n) => <option key={n} value={n}>{n}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <Label>Telefone</Label>
                                        <Input data-testid="collab-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 rounded-xl" placeholder="(11) 90000-0000" />
                                    </div>
                                </div>
                                <div>
                                    <Label>E-mail de acesso</Label>
                                    <Input data-testid="collab-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 rounded-xl" placeholder="colaborador@email.com" />
                                </div>
                                <div>
                                    <Label>Senha inicial</Label>
                                    <Input data-testid="collab-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1 rounded-xl" placeholder="Defina uma senha" />
                                </div>
                                <Button onClick={create} disabled={saving} className="w-full rounded-full" data-testid="save-collaborator-btn">
                                    {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Cadastrar
                                </Button>
                            </div>
                        </DialogContent>
                    </Dialog>
                }
            />

            <Filters q={filters.q} start={filters.start} end={filters.end} onChange={setFilters} placeholder="Buscar por nome, e-mail ou função..." />

            <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3" data-testid="collaborators-grid">
                {list.length === 0 && <p className="text-sm text-muted-foreground">Nenhum colaborador cadastrado.</p>}
                {list.map((c) => (
                    <div key={c.user_id} className="rounded-2xl border border-border bg-white p-6 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] fade-up" data-testid={`collaborator-card-${c.user_id}`}>
                        <div className="flex items-start justify-between">
                            <div className="flex items-center gap-3">
                                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-accent/15 text-lg font-semibold text-accent">
                                    {c.name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
                                </div>
                                <div>
                                    <p className="font-semibold">{c.name}</p>
                                    <span className="rounded-full bg-secondary/40 px-2.5 py-0.5 text-xs font-medium text-secondary-foreground">{c.nivel}</span>
                                </div>
                            </div>
                            <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${c.active ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}>
                                {c.active ? "Ativo" : "Inativo"}
                            </span>
                        </div>
                        <div className="mt-4 space-y-1.5 text-sm text-muted-foreground">
                            <p className="flex items-center gap-2"><Mail className="h-4 w-4" /> {c.email}</p>
                            {c.phone && <p className="flex items-center gap-2"><Phone className="h-4 w-4" /> {c.phone}</p>}
                            <p className="text-xs">Desde {formatDateBR(c.created_at)}</p>
                        </div>
                        <div className="mt-4 flex flex-wrap gap-2 border-t border-border/60 pt-4">
                            <button onClick={() => setEditUser(c)} data-testid={`edit-collab-${c.user_id}`} className="inline-flex items-center gap-2 rounded-xl border border-border px-3 py-2 text-sm hover:bg-muted">
                                <Pencil className="h-4 w-4" /> Editar
                            </button>
                            <button onClick={() => toggleActive(c)} data-testid={`toggle-collab-${c.user_id}`} className="inline-flex items-center gap-2 rounded-xl border border-border px-3 py-2 text-sm hover:bg-muted">
                                <Power className="h-4 w-4" /> {c.active ? "Desativar" : "Ativar"}
                            </button>
                            {me?.is_master && <ImpersonateButton userId={c.user_id} className="px-3" />}
                            <ConfirmDelete onConfirm={() => remove(c.user_id)} testid={`delete-collab-${c.user_id}`} className="ml-auto" title="Excluir colaborador" description={`Remover ${c.name}? Esta ação não pode ser desfeita.`} />
                        </div>
                    </div>
                ))}
            </div>
            {editUser && (
                <UserEditDialog user={editUser} endpoint="/collaborators" open={!!editUser} onOpenChange={(o) => { if (!o) setEditUser(null); }} onSaved={() => qc.invalidateQueries({ queryKey: ["collaborators"] })} niveis={NIVEIS} />
            )}
        </div>
    );
}
